import type { SupplyChainMaterialScopeLimitation, SupplyChainPlan } from '../types/supplyChain';
import type { SiteRagReport } from '../types/siteRag';

export const MATERIAL_SCOPE_HEADING = 'Owner-declared unpriced material scope';
const UNKNOWN_FIELDS = ['quantity', 'cost', 'lead-time'] as const;
const KEYS = new Set(['id', 'title', 'description', 'unknownFields', 'costTreatment', 'sourceDocumentIds']);

/** Validate an optional extension without changing or manufacturing source values. */
export function validateMaterialScopeLimitations(
  value: unknown,
  availableDocumentIds?: ReadonlySet<string>,
): SupplyChainMaterialScopeLimitation[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 25) throw new Error('Material scope limitations must be an array of at most 25 entries.');
  const seen = new Set<string>();
  return value.map((entry): SupplyChainMaterialScopeLimitation => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('Invalid material scope limitation.');
    const row = entry as Record<string, unknown>;
    if (Object.keys(row).some(key => !KEYS.has(key))) throw new Error('Material scope limitations cannot contain numeric or authority fields.');
    for (const [key, max] of [['id', 200], ['title', 200], ['description', 4000]] as const) {
      const v = row[key];
      if (typeof v !== 'string' || !v.trim() || v.length > max || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(v)) throw new Error(`Invalid material scope ${key}.`);
    }
    const id = row.id as string;
    if (seen.has(id)) throw new Error('Material scope IDs must be unique.');
    seen.add(id);
    if (row.costTreatment !== 'excluded-unpriced') throw new Error('Material scope must remain excluded and unpriced.');
    if (!Array.isArray(row.unknownFields) || row.unknownFields.length === 0 || row.unknownFields.some(v => !UNKNOWN_FIELDS.some(field => field === v)) || new Set(row.unknownFields).size !== row.unknownFields.length) throw new Error('Material scope unknown fields are required and must be unique.');
    if (row.sourceDocumentIds !== undefined) {
      if (!Array.isArray(row.sourceDocumentIds) || row.sourceDocumentIds.some(v => typeof v !== 'string' || !v.trim() || v.length > 200) || new Set(row.sourceDocumentIds).size !== row.sourceDocumentIds.length) throw new Error('Invalid material scope source documents.');
      if (availableDocumentIds && row.sourceDocumentIds.some(v => !availableDocumentIds.has(v as string))) throw new Error('Material scope source document is not attached to this site.');
    }
    // Parsed values remain exact, including the owner's literal source wording.
    return entry as SupplyChainMaterialScopeLimitation;
  });
}

/** Public-safe text: IDs and private document lineage are deliberately omitted. */
export function materialScopeLimitationText(plan: Pick<SupplyChainPlan, 'materialScopeLimitations'> | null | undefined): string[] {
  return validateMaterialScopeLimitations(plan?.materialScopeLimitations).map(row => (
    `${row.title}: ${row.description} Unknown: ${row.unknownFields.map(field => field === 'lead-time' ? 'lead time' : field).join(', ')}. `
    + 'Excluded from the priced BOM subtotal; not valued at zero. Resolve the declared unknowns and any required budget or schedule allowance before treating this as a complete equipment budget. '
    + 'This is owner-declared scope, not supplier, OEM or agency confirmation.'
  ));
}

export function materialScopeDisclosure(plan: Pick<SupplyChainPlan, 'materialScopeLimitations'> | null | undefined): string {
  const rows = materialScopeLimitationText(plan);
  return rows.length ? `${MATERIAL_SCOPE_HEADING}\n${rows.join('\n')}` : '';
}

export function withMaterialScopeDisclosure(narrative: string | undefined, plan: Pick<SupplyChainPlan, 'materialScopeLimitations'> | null | undefined): string | undefined {
  const disclosure = materialScopeDisclosure(plan);
  if (!disclosure) return narrative;
  const base = narrative?.trim() ?? '';
  return base.endsWith(disclosure) ? base : [base, disclosure].filter(Boolean).join('\n\n');
}

/** Preserve the complete frozen disclosure when the public projection carries
 * only sanitized canonical prose. This splits display text; it infers no facts,
 * authority, source IDs or numeric rows. */
export function splitMaterialScopeNarrative(text: string | undefined): { narrative: string | undefined; disclosure: string } {
  if (!text) return { narrative: text, disclosure: '' };
  const marker = `${MATERIAL_SCOPE_HEADING}\n`;
  const offset = text.indexOf(`\n${marker}`);
  const start = text.startsWith(marker) ? 0 : offset < 0 ? -1 : offset + 1;
  if (start < 0) return { narrative: text, disclosure: '' };
  return { narrative: text.slice(0, start).trim(), disclosure: text.slice(start) };
}

/** Only the frozen plan is read by canonical/native/Office projections. */
export function frozenProcurementOverview(report: SiteRagReport): string | undefined {
  const narrative = report.supplyChainAnalysis?.procurementOverview?.trim() || report.executiveSummary?.trim();
  return withMaterialScopeDisclosure(narrative, report.supplyChainPlanAtGen);
}

/** Deterministic producer supplement; called before validation and source hashing. */
export function applyMaterialScopeToReport(report: SiteRagReport, plan: SupplyChainPlan | null | undefined): void {
  const disclosure = materialScopeDisclosure(plan);
  if (report.reportType !== 'supply-chain-risk' || !disclosure) return;
  if (!report.supplyChainAnalysis) throw new Error('Supply-chain analysis is required before material scope can be frozen.');
  report.supplyChainAnalysis = {
    ...report.supplyChainAnalysis,
    procurementOverview: withMaterialScopeDisclosure(report.supplyChainAnalysis.procurementOverview?.trim() || report.executiveSummary?.trim(), plan) ?? disclosure,
  };
}
